import React, { useState, useEffect } from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Button } from './components/ui/button';
import { Menu, X } from 'lucide-react';
import Home from './pages/Home';
import Resources from './pages/Resources';
import ArticlePage from './pages/ArticlePage';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import { Toaster } from './components/ui/sonner';
import "./content/articles/articles.css";
import ScrollToTop from "./components/ScrollToTop";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToServices = () => {
    if (location.pathname !== '/') {
      window.location.href = '/#services';
    } else {
      document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { name: 'Home', path: '/', scroll: false },
    { name: 'Services', path: '/', scroll: true, action: scrollToServices },
    { name: 'Resources', path: '/resources', scroll: false },
    { name: 'Contact', path: '/contact', scroll: false },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
    }`}>
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/venura-logo.png" alt="Venura Consultants" className="h-8" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              link.scroll ? (
                <button
                  key={link.name}
                  onClick={link.action}
                  className="text-gray-700 hover:text-[#00607A] font-medium transition-colors duration-300"
                >
                  {link.name}
                </button>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`font-medium transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-[#00607A]'
                      : 'text-gray-700 hover:text-[#00607A]'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
            <Link to="/contact">
              <Button className="bg-[#00607A] hover:bg-[#005066] text-white transition-all duration-300">
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700 hover:text-[#00607A] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                link.scroll ? (
                  <button
                    key={link.name}
                    onClick={link.action}
                    className="text-left text-gray-700 hover:text-[#00607A] font-medium transition-colors duration-300 py-2"
                  >
                    {link.name}
                  </button>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`font-medium transition-colors duration-300 py-2 ${
                      location.pathname === link.path
                        ? 'text-[#00607A]'
                        : 'text-gray-700 hover:text-[#00607A]'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-[#00607A] hover:bg-[#005066] text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#002029] text-white">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="md:col-span-2">
            <img src="/venura-logo.png" alt="Venura Consultants" className="h-8 mb-4" />
            <p className="text-gray-400 mb-4 leading-relaxed">
              Your strategic partner in growth. We help businesses make better decisions with clear numbers, bringing structure, clarity, and growth readiness.
            </p>
            <div className="space-y-2">
              <p className="text-gray-400">
                <a href="mailto:admin@venuraconsultants.com" className="hover:text-[#00607A] transition-colors">
                  admin@venuraconsultants.com
                </a>
              </p>
              <p className="text-gray-400">
                <a href="https://www.venuraconsultants.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#00607A] transition-colors">
                  www.venuraconsultants.com
                </a>
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-[#00607A] transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <button 
                  onClick={() => {
                    if (window.location.pathname !== '/') {
                      window.location.href = '/#services';
                    } else {
                      document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="text-gray-400 hover:text-[#00607A] transition-colors"
                >
                  Services
                </button>
              </li>
              <li>
                <Link to="/resources" className="text-gray-400 hover:text-[#00607A] transition-colors">
                  Resources
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-[#00607A] transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Services</h3>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => {
                    if (window.location.pathname !== '/') {
                      window.location.href = '/#services';
                    } else {
                      document.getElementById('service-accounting')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="text-gray-400 hover:text-[#00607A] transition-colors"
                >
                  Accounting & Compliance
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    if (window.location.pathname !== '/') {
                      window.location.href = '/#services';
                    } else {
                      document.getElementById('service-reporting')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="text-gray-400 hover:text-[#00607A] transition-colors"
                >
                  Reporting & Insights
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    if (window.location.pathname !== '/') {
                      window.location.href = '/#services';
                    } else {
                      document.getElementById('service-diligence')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="text-gray-400 hover:text-[#00607A] transition-colors"
                >
                  Due Diligence
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    if (window.location.pathname !== '/') {
                      window.location.href = '/#services';
                    } else {
                      document.getElementById('service-advisory')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="text-gray-400 hover:text-[#00607A] transition-colors"
                >
                  Operational Advisory
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © {currentYear} Venura Consultants. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <a 
                href="https://www.linkedin.com/company/108459329/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#00607A] transition-colors text-sm"
              >
                LinkedIn
              </a>
              <span className="text-gray-600">|</span>
              <Link to="/privacy" className="text-gray-400 hover:text-[#00607A] transition-colors text-sm">
                Privacy Policy
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/terms" className="text-gray-400 hover:text-[#00607A] transition-colors text-sm">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Navigation />

        <div className="pt-20">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/resources/:slug" element={<ArticlePage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
          </Routes>

          <Footer />
        </div>

        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
